import { useCallback, useEffect, useState } from 'react'

import type { FeedItem } from '@knocklabs/client'

import { ANNOUNCEMENT_REGISTRY } from '@/components/announcements/registry'
import type {
  AnnouncementConfig,
  AnnouncementEligibilityContext,
  ChangelogFeedItemData,
} from '@/components/announcements/types'

import { CHANGELOG_WORKFLOW_KEY } from '@/constants/knock'

import { useActiveOrgHasCuiEnabled } from '@/hooks/organization/useActiveOrg'

import { useKnockFeed } from '@/notifications/knock/components/NotificationProvider'

interface QueuedAnnouncement {
  item: FeedItem
  data: ChangelogFeedItemData
  config: AnnouncementConfig
}

const byPublishedThenInserted = (
  a: QueuedAnnouncement,
  b: QueuedAnnouncement,
) => {
  const byDate = (a.data.published_at ?? '').localeCompare(
    b.data.published_at ?? '',
  )
  if (byDate !== 0) return byDate
  return a.item.inserted_at.localeCompare(b.item.inserted_at)
}

// Freeze the eligible, unread, opted-in announcements into an ordered queue
// (oldest first). Only entries in the registry are considered, and each entry's
// own eligibility predicate gets the final say.
const buildAnnouncementQueue = (
  items: FeedItem[],
  ctx: AnnouncementEligibilityContext,
): QueuedAnnouncement[] => {
  const queue: QueuedAnnouncement[] = []
  for (const item of items) {
    if (item.source.key !== CHANGELOG_WORKFLOW_KEY) continue
    if (item.read_at !== null || item.archived_at !== null) continue
    const data = (item.data ?? {}) as ChangelogFeedItemData
    if (data.entry_id === undefined) continue
    const config = ANNOUNCEMENT_REGISTRY[data.entry_id]
    if (config === undefined) continue
    if (config.isEligible !== undefined && !config.isEligible(ctx)) continue
    queue.push({ item, data, config })
  }
  return queue.sort(byPublishedThenInserted)
}

export interface AnnouncementQueue {
  current: QueuedAnnouncement | null
  dismiss: () => void
}

/**
 * Drives the app-wide announcement modal off the Knock in-app feed. Snapshots
 * the eligible unread announcements per eligibility context (feed loaded + CUI
 * status known; re-snapshots when that changes, e.g. on org switch) and walks
 * them one at a time — durable because "seen" is Knock's server-side read
 * state, not localStorage. New feed items that arrive mid-session are
 * otherwise intentionally ignored until the next mount ("fire on load").
 */
export const useAnnouncementQueue = (): AnnouncementQueue => {
  const { feedClient } = useKnockFeed()
  const { data: cuiEnabled } = useActiveOrgHasCuiEnabled()
  const [queue, setQueue] = useState<QueuedAnnouncement[] | null>(null)
  const [cursor, setCursor] = useState(0)

  useEffect(() => {
    // Wait for a definite CUI answer so eligibility isn't decided on undefined.
    if (typeof cuiEnabled !== 'boolean') return
    let cancelled = false
    // The bell only fetches lazily on open, so fetch here to guarantee the feed
    // is populated, then snapshot from the freshly-loaded store state. We
    // re-snapshot whenever the eligibility context (CUI status) changes — e.g.
    // on org switch — so an org the feature is unavailable to can't keep showing
    // a queued modal, and an org it just became available to still gets one.
    // Dismissed entries stay dismissed because they're read server-side and get
    // filtered back out on rebuild.
    feedClient
      .fetch()
      .then(() => {
        if (cancelled) return
        setQueue(
          buildAnnouncementQueue(feedClient.getState().items, { cuiEnabled }),
        )
        setCursor(0)
      })
      .catch(() => {
        if (cancelled) return
        // Feed fetch failed: settle on an empty queue rather than hanging on
        // `null` forever (which reads the same — no modal — but is recoverable
        // if the context later changes and the effect re-runs).
        setQueue([])
      })
    return () => {
      cancelled = true
    }
  }, [feedClient, cuiEnabled])

  const dismiss = useCallback(() => {
    // Effect stays outside the setState updater (updaters must be pure). A
    // failed mark is swallowed: the item then stays unread server-side, so the
    // announcement simply re-fires on the next load instead of breaking this
    // one. A double-fired dismiss re-marks the same (idempotent) item rather
    // than silently consuming the next, never-shown announcement.
    const announcement = queue?.[cursor]
    if (announcement !== undefined) {
      void feedClient.markAsRead(announcement.item).catch(() => {})
    }
    setCursor((current) => current + 1)
  }, [feedClient, queue, cursor])

  return { current: queue?.[cursor] ?? null, dismiss }
}
